import React from 'react';
import Drawer, { FooterSpacer } from '../Drawer';
import Button from '../Button';
import { useMoneyFormat, useDateFormat, useDatePattern } from '../../context/LocaleContext';
import { formatClock } from '../../domain/packageTemplates';
import {
  activityDelta, activityDescription, bookedRowLabel, bookedRowTime, coveredThroughText,
  expiringText, forfeitedText, packageName, packageNamesText, runsOutText,
  showsPackageColumn, unmarkedBehindText,
} from '../../domain/packageSale';
import s from './drawers.module.css';
import t from '../SimpleTable.module.css';
import Unread from '../Unread';
import { READING_WORDING as RW } from '../../domain/reading';

// MMS's own row labels: `Package Added - <template>`, `Package Added -
// <template> - Expires <date>`, `Lesson`, `Expiry`. Ours names the template on
// a purchase as theirs does — a student holding two packages has two
// interleaved sets of rows — and keeps the price beside it, which MMS does
// not show here. The price carries its currency symbol, so a figure in yen
// does not read as dollars (src/domain/locale.js).
//
// The row words — this, the signed minutes, the projected lesson's label and
// the sentences over and under the table — are src/domain/packageSale.js's,
// shared with the phones.
const describe = (a, formatMoney) => activityDescription(a, a.priceMinor ? formatMoney(a.priceMinor) : '');

// Signed minutes: every kind carries its own amount (#181), as MMS's rows read
// `Usage: 30 min` and `Added: 2 hr 30 min`. `activityDelta`.

// What a projected row says it is. A past unmarked lesson is called that
// outright rather than `Lesson booked`, because "booked" reads as future: it
// is a lesson that happened and nobody has said what happened at it (#347).
// `bookedRowLabel`.

// Package Activity (#181).
//
// As in MMS: four columns — `Date & Time | Activity | Change | Balance` — in
// minutes, with booked occurrences dated in the future as well as taught ones,
// each with the running balance after it, so the lesson where the package
// runs out is findable. Newest first, so the booked lessons are at the top.
//
// MMS's dialog also carries a date-range filter (defaulting to the sale's
// start date through its expiry date, with presets), Sort and Search; this
// drawer has none of them.
//
// The projected `Expiry` row (#368), as MMS draws it: dated, carrying what is
// forfeited, with the running balance after it — `10/17/2026 · Expiry ·
// Expiry: 1 hr · 0h 00m` — styled as this app's warning rather than MMS's
// red.
//
// The `Package` column is ours: with two packages one interleaved balance
// cannot say which one a lesson spent. The draw order — earliest expiry first
// — is ours too (`core/src/packages.rs` has the decision).
//
// ── What the forecast cannot see (#347) ─────────────────────────────────────
//
// The forecast reaches only as far as the window `useStudentPackageForecast`
// folds — 90 days — so `runsOutOn: null` means "nothing booked in the next 90
// days outruns the package", and the sentence below names the horizon it
// looked to.
//
// A past lesson nobody has marked is in the table, dated where it is, labelled
// as unmarked and counted in `unmarkedBehind` on the rail, because it will draw
// on the package when attendance is taken. The sentence above the table says
// how many, since newest-first buries them below the bookings.
//
// `read`: the package ledger has answered (#629). Until it has, an empty log
// says nothing rather than "Nothing yet."
export default function PackageActivityDrawer({ open, activity, read = true, failed = false, forecast = null, onClose }) {
  const formatMoney = useMoneyFormat();
  // Every date here — the Date column and the ones inside the sentences — is
  // the studio's short date, as MMS writes `9/5/2026` for a US studio; the
  // sentences are the core's, drawn in the same pattern (#510).
  const formatDate = useDateFormat();
  const datePattern = useDatePattern();
  const byId = new Map(activity.map((a) => [a.id, a]));
  const expiring = activity.filter((a) => a.kind === 'purchase' && a.expiresOn);

  // The core's order and balances, rejoined against the documents the caller
  // holds (`packageForecast` in core-ffi, `activityFor` in usePackages.jsx).
  //
  // A row whose id is in neither map is dropped: the fold walks every entry,
  // tombstoned ones included, while this drawer is handed the live ones only.
  // A tombstoned entry moves no balance, so the surviving rows are unchanged.
  const rows = forecast
    ? forecast.rows
      .map((r) => {
        // A projected expiry has no document behind it (#368), so its date
        // comes off the purchase it emptied, which the caller already holds.
        if (r.expiry) {
          const purchase = byId.get(r.packageIds[0]);
          return purchase ? { ...r, date: purchase.expiresOn } : null;
        }
        if (!r.booked) {
          const entry = byId.get(r.id);
          return entry ? { ...r, date: entry.occurredOn, entry } : null;
        }
        const lesson = forecast.lessonById.get(r.id);
        // The occurrence's duration, which is what it will take out of the
        // package — the number the fold was handed (#181).
        return lesson
          ? { ...r, date: lesson.date, minutes: Math.max(0, Number(lesson.durationMin) || 0) }
          : null;
      })
      .filter(Boolean)
    : activity.map((a) => ({
      id: a.id, date: a.occurredOn, entry: a, balance: null, packageIds: [],
    }));

  // Which package a row drew on, named (#368).
  //
  // The column renders only with more than one package, the only state in
  // which the question exists, so the ordinary student reads MMS's four
  // columns.
  //
  // A purchase names itself: the column asks which package a row is about,
  // and a sale is about the package it created (the fold leaves `packageIds`
  // empty there, because a purchase draws on nothing).
  //
  // A sale keeps its own copy of the template's name (#181), so this cannot go
  // blank when a template is renamed or deleted. It falls back to the sale's
  // date for a package sold without a name, because two packages must read
  // differently.
  const showPackage = showsPackageColumn(forecast);
  const nameOf = (id) => packageName(byId.get(id), datePattern);
  const drewOn = (r) => {
    if (r.entry?.kind === 'purchase') return nameOf(r.id);
    if (!r.packageIds?.length) return '';
    return packageNamesText(r.packageIds.map(nameOf));
  };
  const runsOut = runsOutText(forecast, datePattern);
  const covered = coveredThroughText(forecast, datePattern);
  const unmarked = unmarkedBehindText(forecast);
  const expiringNote = expiringText(expiring.map((e) => e.expiresOn), datePattern);
  const forfeited = forfeitedText(forecast);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Package Activity"
      width={620}
      footer={<><FooterSpacer /><Button onClick={onClose}>Close</Button></>}
    >
      {/* On `activity`, not on `rows`: a student with no package still has
          booked lessons, and the fold projects them, so keying on `rows` would
          open on a table of uncovered lessons for a package nobody sold. */}
      {activity.length === 0 && !read ? (
        <Unread failed={failed} />
      ) : activity.length === 0 ? (
        <div className={s.empty}>{RW['packages.activityEmpty']}</div>
      ) : (
        <>
          {runsOut ? <div className={s.notice}>{runsOut}</div> : null}
          {/* The covered case, saying how far it looked (#347). Only with a
              forecast that has a horizon to name; a caller that wired none gets
              the plain log. */}
          {covered ? <div className={s.hint}>{covered}</div> : null}
          {/* Minutes (#181), so the figure is `1h 30m` and is not pluralised:
              the balance above is provisional by exactly this much. */}
          {unmarked ? <div className={s.notice}>{unmarked}</div> : null}
          <div className={`${t.wrap} ${s.tableWrap}`}>
            <table className={t.table}>
              <thead>
                <tr>
                  <th>Date</th><th>Activity</th>
                  {showPackage ? <th>Package</th> : null}
                  <th>Time</th><th>Balance</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>{formatDate(r.date) || r.date}</td>
                    <td>
                      {/* Three kinds of row; the expiry has no document behind
                          it, and MMS labels it `Expiry` too. */}
                      {r.expiry ? 'Expiry' : (r.entry ? describe(r.entry, formatMoney) : bookedRowLabel(r))}
                    </td>
                    {showPackage ? <td>{drewOn(r)}</td> : null}
                    <td>
                      {r.entry ? activityDelta(r.entry) : bookedRowTime(r, r.minutes)}
                    </td>
                    <td>{r.balance == null ? '' : formatClock(r.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* What the `Expiry` rows above mean, said once: the remainder is
              forfeited, as in MMS. The dates are named because a package that
              expires after the horizon has no projected row (#347). */}
          {expiringNote ? <div className={s.hint}>{expiringNote}</div> : null}
          {/* Already gone, rather than about to be: the balance a teacher reads
              is lower than the sales add up to, and this says why. */}
          {forfeited ? <div className={s.notice}>{forfeited}</div> : null}
        </>
      )}
    </Drawer>
  );
}
